import React, { useEffect, useState, useRef } from 'react';

import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { PrimaryButton, ButtonIcon, SecondaryButton } from '@hz-design-system/web-ui';

import Classes from './SaveSearchButton.module.scss';

type TSaveSearchButtonProps = {
  onClick: () => void;
  isSearchSaved: boolean;
  smallButton: boolean;
  isTabletAdaptingMobile?: boolean;
  shouldShowFloatingButton?: boolean;
};

const SCROLL_STOP_TIMEOUT = 800; // ms
const SCROLL_THRESHOLD = 300; // px

const SaveSearchButton = ({
  onClick,
  isSearchSaved,
  smallButton,
  isTabletAdaptingMobile,
  shouldShowFloatingButton,
}: TSaveSearchButtonProps) => {
  const { t } = useI18nContext();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const [, setScrollYPosition] = useState(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingUpRef = useRef(false);
  const tickingRef = useRef(false);
  const saveText = smallButton ? t('saveSearch.buttonSaveShort') : t('saveSearch.buttonSaveLong');
  const savedText = smallButton ? t('saveSearch.buttonSavedShort') : t('saveSearch.buttonSavedLong');
  const buttonText = isSearchSaved ? savedText : saveText;
  const largeButton = !smallButton;

  const shouldShowPrimaryButton = () => {
    return shouldShowFloatingButton || !isTabletAdaptingMobile;
  };

  const ButtonKind = shouldShowPrimaryButton() ? PrimaryButton : SecondaryButton;

  useEffect(() => {
    isScrollingUpRef.current = isScrollingUp;
  }, [isScrollingUp]);

  useEffect(() => {
    if (!shouldShowFloatingButton || largeButton) return;

    const scrollLogic = () => {
      const currentScrollPosition = window.scrollY;
      const shouldCollapse = currentScrollPosition > SCROLL_THRESHOLD;

      if (!isScrollingUpRef.current || shouldCollapse) {
        setIsCollapsed(shouldCollapse);
      }

      setScrollYPosition((previousScrollPosition) => {
        const scrollingUp = previousScrollPosition > currentScrollPosition;
        setIsScrollingUp(scrollingUp);
        return currentScrollPosition;
      });

      if (scrollTimeoutRef.current !== null) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(function () {
        if (isScrollingUpRef.current) {
          // If the user is scrolling up and stops, we want to show the button again
          setIsCollapsed(false);
        }
      }, SCROLL_STOP_TIMEOUT);
    };

    const handleScroll = () => {
      if (!tickingRef.current) {
        // instead of a static throttle (ie 60 fps), we rely on dynamic fps of the device's screen
        window.requestAnimationFrame(() => {
          scrollLogic();
          tickingRef.current = false;
        });

        tickingRef.current = true;
      }
    };

    document.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('scroll', handleScroll);

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [shouldShowFloatingButton, largeButton]);

  const handleClick = () => {
    onClick();
  };

  return (
    <ButtonKind
      onClick={handleClick}
      className={`${shouldShowFloatingButton ? Classes.floatingSaveSearchButton : ''} ${isCollapsed ? Classes['floatingSaveSearchButton--collapsed'] : ''}`}
      data-testid={isSearchSaved ? 'savedSearchButton' : 'saveSearchButton'}
    >
      <ButtonIcon name={isSearchSaved ? 'search-saved' : 'search-save'} />
      <span
        className={`${Classes.saveSearchButtonText} ${isCollapsed ? Classes['saveSearchButtonText--collapsed'] : ''}`}
      >
        {buttonText}
      </span>
    </ButtonKind>
  );
};

export default SaveSearchButton;
